import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, EyeIcon, MessageSquareIcon } from "lucide-react";
import { useToggle } from "react-use";
import { ChatMessageBubble } from "@/components/chat/ChatMessageBubble";
import { ActionButtonGroup, CopyButton } from "@/components/chat/actions";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { ScrollableChat } from "@/components/feature/chat/ScrollableChat";
import { usePinnedTurn } from "@/components/feature/chat/usePinnedTurn";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { CHAT_ANIMATION, MESSAGE_STYLES } from "@/components/chat/constants";
import { Button } from "@/components/ui/button";
import { useStreamingText } from "@/hooks/useStreamingText";
import { cn } from "@/lib/utils";
import { extractArtefactsFromString, groupIntoTurns } from "@/modules/chat/chat.utils";
import type { ChatMessage } from "@/components/chat/types";
import type { QaProgressMetrics } from "./useQaProgress";
import type { Citation, ListResult, SnapshotConversationMessage } from "./types";
import { SnapshotMarkdown } from "./SnapshotMarkdown";
import { SnapshotQaProgress } from "./SnapshotQaProgress";

const parseListResults = (content: string): ListResult[] => {
  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    console.error("Failed to parse LIST results:", content.slice(0, 200));
    return [];
  }
};

const SourceLabel: React.FC<{ source: string }> = ({ source }) => (
  <div className="mb-2 text-[10px] text-muted-foreground">
    {source === "history" ? "Answered from conversation history" : "Answered from document data"}
  </div>
);

interface QaListResultsProps {
  content: string;
  onOutcomeClick: (outcomeId: string) => void;
  onOpenQa: (outcomeId: string) => void;
}

const QaListResults: React.FC<QaListResultsProps> = ({ content, onOutcomeClick, onOpenQa }) => {
  const results = React.useMemo(() => parseListResults(content), [content]);

  if (results.length === 0) return null;

  return (
    <motion.div
      className="mb-3 w-full"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="overflow-hidden ring-1 ring-[#d2cac0] sm:rounded-lg">
        <table className="w-full divide-y divide-gray-300">
          <thead className="border bg-[#f1edea] text-black">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                Results ({results.length} documents)
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                <div className="text-center">Actions</div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            <AnimatePresence mode="popLayout">
              {results.map((result, index) => (
                <motion.tr
                  key={result.outcome_id ?? result.document_name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <td className="py-4 pl-4 pr-3 text-sm sm:pl-6">
                    <div className="font-medium text-gray-900">{result.document_name}</div>
                    <div className="mt-2 text-sm text-gray-500">{result.explanation}</div>
                  </td>
                  <td className="whitespace-nowrap pl-3">
                    {result.outcome_id && (
                      <div className="flex justify-end gap-1 pr-2">
                        <Button
                          variant="second-white"
                          size="sm"
                          className="flex items-center gap-2 px-3 py-2 text-xs"
                          type="button"
                          title="View Snapshot"
                          onClick={() => onOutcomeClick(result.outcome_id!)}
                        >
                          <EyeIcon className="h-4 w-4 flex-shrink-0" />
                        </Button>
                        <Button
                          variant="second-white"
                          size="sm"
                          className="flex items-center gap-2 px-3 py-2 text-xs"
                          type="button"
                          title="Q&A"
                          onClick={() => onOpenQa(result.outcome_id!)}
                        >
                          <MessageSquareIcon className="h-4 w-4 flex-shrink-0" />
                        </Button>
                      </div>
                    )}
                  </td>
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

const CollapsedListResult: React.FC<{
  content: string;
  onOutcomeClick: (outcomeId: string) => void;
  onOpenQa: (outcomeId: string) => void;
}> = ({ content, onOutcomeClick, onOpenQa }) => {
  const [expanded, toggleExpanded] = useToggle(false);
  const results = React.useMemo(() => parseListResults(content), [content]);

  if (results.length === 0) return null;

  return (
    <div className="mb-3 w-full">
      <button
        type="button"
        onClick={toggleExpanded}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-ivory-mist-100 hover:text-foreground"
      >
        <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
        {results.length} document{results.length !== 1 ? "s" : ""} listed
      </button>
      {expanded && <QaListResults content={content} onOutcomeClick={onOutcomeClick} onOpenQa={onOpenQa} />}
    </div>
  );
};

interface QaMessageProps {
  message: SnapshotConversationMessage;
  onCitationClick: (citation: Citation) => void;
  onOutcomeClick: (outcomeId: string) => void;
  onOpenQa: (outcomeId: string) => void;
}

const QaMessage = React.memo<QaMessageProps>(({ message, onCitationClick, onOutcomeClick, onOpenQa }) => {
  const chatMessage: ChatMessage = {
    id: message.id,
    type: message.role === "human" ? "human" : "ai",
    content: message.content,
  };

  const hasCitations = message.role === "assistant" && message.citations && message.citations.length > 0;

  if (!hasCitations) {
    return (
      <ChatMessageBubble
        message={chatMessage}
        actions={message.role === "assistant" ? { showCopy: true } : {}}
        hideActions={message.role === "human"}
      />
    );
  }

  if (message.mode === "LIST") {
    return <QaListResults content={message.content} onOutcomeClick={onOutcomeClick} onOpenQa={onOpenQa} />;
  }

  return (
    <motion.div
      className="mb-3 w-full"
      initial={CHAT_ANIMATION.fadeIn}
      animate={CHAT_ANIMATION.visible}
      exit={CHAT_ANIMATION.fadeOut}
      transition={CHAT_ANIMATION.spring}
    >
      <div className={MESSAGE_STYLES.ai}>
        <SnapshotMarkdown content={message.content} citations={message.citations!} onCitationClick={onCitationClick} />
      </div>
      <ActionButtonGroup>
        <CopyButton content={message.content} message={chatMessage} />
      </ActionButtonGroup>
    </motion.div>
  );
});

QaMessage.displayName = "QaMessage";

interface StreamingMessageProps {
  content: string;
  isStreaming: boolean;
}

const StreamingMessage: React.FC<StreamingMessageProps> = ({ content, isStreaming }) => {
  const smoothContent = useStreamingText(content, isStreaming, 3);
  const [processedContent, artefacts] = React.useMemo(() => extractArtefactsFromString(smoothContent), [smoothContent]);

  return (
    <motion.div
      className="mb-3 w-full"
      initial={CHAT_ANIMATION.fadeIn}
      animate={CHAT_ANIMATION.visible}
      transition={CHAT_ANIMATION.spring}
      layout={false}
    >
      <div className={MESSAGE_STYLES.ai}>
        {smoothContent && (
          <MarkdownRenderer
            content={processedContent}
            evidences={[]}
            processCitations={false}
            inlineArtefacts={artefacts}
            isStreaming={isStreaming}
          />
        )}
        <AnimatePresence>
          {isStreaming && (
            <div className="text-coral">
              <TypingIndicator />
            </div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const isCompletedList = (message: SnapshotConversationMessage) =>
  message.mode === "LIST" && message.role === "assistant" && !!message.citations?.length;

interface Props {
  messages: SnapshotConversationMessage[];
  lastSentMessageId: string | null;
  streamingContent: string;
  isProcessing: boolean;
  onCitationClick: (citation: Citation) => void;
  onOutcomeClick: (outcomeId: string) => void;
  onOpenQa: (outcomeId: string) => void;
  progressMetrics?: QaProgressMetrics;
  streamingListResults?: ListResult[];
  streamingSource?: string | null;
}

export const SnapshotQaMessages: React.FC<Props> = ({
  messages,
  lastSentMessageId,
  streamingContent,
  isProcessing,
  onCitationClick,
  onOutcomeClick,
  onOpenQa,
  progressMetrics,
  streamingListResults,
  streamingSource,
}) => {
  const { chatRef, pinnedTurnRef } = usePinnedTurn(lastSentMessageId);

  // A loaded conversation opens at its latest message.
  React.useEffect(() => {
    if (!lastSentMessageId && messages.length > 0) {
      chatRef.current?.scrollToBottom();
    }
  }, [chatRef, lastSentMessageId, messages.length]);

  const lastMessage = messages[messages.length - 1];
  const lastIsCompletedList = lastMessage && isCompletedList(lastMessage);

  const messagesBefore = lastIsCompletedList ? messages.slice(0, -1) : messages;
  const turns = groupIntoTurns(messagesBefore, message => message.role === "human");

  // The answer in progress belongs to the latest turn, so it streams below the pinned prompt.
  const latestTurnFooter = (
    <>
      {progressMetrics && <SnapshotQaProgress metrics={progressMetrics} />}
      {lastIsCompletedList && (
        <QaMessage
          key={lastMessage.id}
          message={lastMessage}
          onCitationClick={onCitationClick}
          onOutcomeClick={onOutcomeClick}
          onOpenQa={onOpenQa}
        />
      )}
      {streamingListResults && streamingListResults.length > 0 && (
        <QaListResults
          content={JSON.stringify(streamingListResults)}
          onOutcomeClick={onOutcomeClick}
          onOpenQa={onOpenQa}
        />
      )}
      <AnimatePresence>
        {isProcessing && <StreamingMessage key="streaming" content={streamingContent} isStreaming={isProcessing} />}
      </AnimatePresence>
      {streamingSource && !isProcessing && <SourceLabel source={streamingSource} />}
    </>
  );

  return (
    <div className="min-h-0 flex-1">
      <ScrollableChat ref={chatRef} className="px-4 py-3">
        {turns.map((turn, index) => {
          const isLatest = index === turns.length - 1;
          const isPinned = isLatest && lastSentMessageId !== null;

          return (
            // Keyed by position: the saved copy of a prompt replaces the local one under a new id,
            // and that must not remount the pinned turn.
            <div key={index} ref={isPinned ? pinnedTurnRef : undefined} className={cn(isPinned && "min-h-full")}>
              {turn.map(message =>
                isCompletedList(message) ? (
                  <CollapsedListResult
                    key={message.id}
                    content={message.content}
                    onOutcomeClick={onOutcomeClick}
                    onOpenQa={onOpenQa}
                  />
                ) : (
                  <QaMessage
                    key={message.id}
                    message={message}
                    onCitationClick={onCitationClick}
                    onOutcomeClick={onOutcomeClick}
                    onOpenQa={onOpenQa}
                  />
                ),
              )}
              {isLatest && latestTurnFooter}
            </div>
          );
        })}
        {turns.length === 0 && latestTurnFooter}
      </ScrollableChat>
    </div>
  );
};
